import React, {forwardRef, useCallback, useState} from "react";
import {IProtocolStep, IProtocolStepKind, TProtocolStepId,} from "../../types/protocol";
import {Box, BoxProps, Text,} from "@mantine/core";
import {StepView, TStepPartialUpdate} from "../../common/steps/StepView";
import {IEvent, TEventId, TSubjectId} from "@/common/types";
import {EventRecorder} from "@/common/components/EventRecorder";
import {IconChevronDown, IconChevronUp} from "@tabler/icons-react";
import cx from "clsx";
import stepClasses from "../../common/steps/StepView.module.css";
import classes from "./RecorderStepView.module.css";
import commonClasses from "../../../common/common.module.css";
import {EventListView} from "@/protocols/common/components/EventListView";
import {DeepPartial} from "@/utils/types/DeepPartial";
import {IProtocolStepEvent} from "@/protocols/types/IProtocolStepEvent.ts";
import {Instructions} from "@/protocols/common/steps/fields";
import {StepTitleBar} from "@/protocols/common/steps/views/components/StepTitleBar.tsx";
import {useWriteStatusStore} from "@/protocols/persistence";


interface RecorderStepViewProps extends BoxProps {
    step: IProtocolStep;
    index: number;
    subjectId: TSubjectId;
    protocolStepEvents: Record<TProtocolStepId, Array<IEvent>>;
    onDeleteEvent: (id: TEventId) => void
    timezone: string;
    selected?: boolean;
    onSelect?: () => void;
    onChange: (updates: DeepPartial<IProtocolStep>) => void;
    titleComponent?: React.ReactNode; // Optional custom title component
    focusMode?: boolean
    /**
     * Single creation hook forwarded to the rendered EventRecorder. The
     * caller decides how to persist (pending-writes, direct, etc).
     */
    createEvent?: (
        eventData: IProtocolStepEvent
    ) => Promise<IProtocolStepEvent>
}

export const RecorderStepView = forwardRef<HTMLDivElement, RecorderStepViewProps>(({
                                                                                       step,
                                                                                       index,
                                                                                       subjectId,
                                                                                       protocolStepEvents,
                                                                                       onDeleteEvent,
                                                                                       timezone,
                                                                                       selected = false,
                                                                                       onSelect,
                                                                                       onChange,
                                                                                       titleComponent,
                                                                                       focusMode = false,
                                                                                       createEvent,

                                                                                       ...others
                                                                                   }, ref) => {
    // Pass partial updates from StepView directly to onChange
    const handleStepViewChange = useCallback((updates: TStepPartialUpdate) => {
        onChange(updates as DeepPartial<IProtocolStep>);
    }, [onChange]);

    const [isEventRecorderExpanded, setIsEventRecorderExpanded] = useState(false);
    const formResetEpoch = useWriteStatusStore(s => s.formResetEpoch)
    const renderInstructionView = () => {
        if (step.instructions && step.kind !== IProtocolStepKind.INSTRUCTIONS && step.kind !== IProtocolStepKind.GROUP) {
            return (<Instructions instructions={step.instructions || ""} editingMode="checkboxes" expanded={focusMode}
                                  onChange={(newInstructions) => onChange({instructions: newInstructions})}/>)
        } else {
            return (<></>)
        }
    }
    return (
        <Box
            ref={ref}
            data-testid="recorder-step-view"
            className={cx(stepClasses.row, !focusMode && commonClasses.hover, {[stepClasses.selected]: selected})}
            onClick={onSelect}
            {...others}
        >
            {/* Row 1: Step number, icon, name, status */}
            <StepTitleBar step={step} onChange={onChange} workflow={"session"} titleComponent={titleComponent} focusMode={focusMode} timezone={timezone}/>
            {renderInstructionView()}
            {/* Row 2: Inline step view fields - all step content is rendered here */}
            <Box className={stepClasses.stepViewRow}>
                <StepView
                    key={formResetEpoch}
                    step={step}
                    subjectId={subjectId}
                    timezone={timezone}
                    workflow={"session"}
                    focusMode={focusMode}
                    onChange={handleStepViewChange}
                />
            </Box>

            {/* Collapsible EventRecorder bar */}
            <Box className={classes.eventRecorderContainer}>
                {isEventRecorderExpanded ? (
                    <Box className={classes.eventRecorderExpanded}>
                        <Box className={classes.collapseButton}
                             onClick={() => setIsEventRecorderExpanded(false)}>
                            <Text size={"xs"}>Step Events</Text>
                            <IconChevronUp size="1em"/>
                        </Box>
                        <EventRecorder
                            key={`event-recorder-${formResetEpoch}`}
                            subjectId={subjectId}
                            protocolId={step.protocolId}
                            stepId={step.id}
                            timezone={timezone}
                            autoStart={true}
                            createEvent={createEvent}
                        />
                        <EventListView
                            key={`event-list-${formResetEpoch}`}
                            timezone={timezone}
                            events={protocolStepEvents[step.id] || []}
                            onDeleteEvent={onDeleteEvent}
                            readonly={true}
                            canDelete={true}
                        />
                    </Box>
                ) : (
                    <Box className={classes.eventRecorderBar}
                         onClick={() => setIsEventRecorderExpanded(true)}>
                        <Box className={classes.collapseButton}>
                            <Text size={"xs"}>Step Events</Text>
                            <IconChevronDown size="1em"/>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box>
    );
});